import { useState, useEffect, useCallback, useRef } from 'react';
import {
  FLIGHT_SESSION_EXPIRES_AT_KEY,
  FLIGHT_SESSION_ALLOWED_EXPIRES_AT_KEY,
  FLIGHT_SEARCH_TIMESTAMP_KEY,
} from '../constants/flightSession.constants';
import { SESSION_TIMER_PRODUCT } from '../constants/sessionTimer.constants';
import { getSessionRemainingMs } from '../utils/sessionTimer.utils';

export default function useTraceTimer(onExpired) {
  const firedRef = useRef(false);

  const getRemaining = useCallback(() => (
    getSessionRemainingMs({ product: SESSION_TIMER_PRODUCT.FLIGHT })
  ), []);

  const [remaining, setRemaining] = useState(getRemaining);

  // When remaining is null (no timestamp yet) or 0 (checkout window ended),
  // poll sessionStorage so a later backend expiry (payment retry) can restart.
  useEffect(() => {
    if (remaining !== null && remaining > 0) return;

    const pollId = setInterval(() => {
      const left = getRemaining();
      if (left != null && left > 0) {
        firedRef.current = false;
        setRemaining(left);
      } else if (remaining === null && left !== null) {
        setRemaining(left);
      }
    }, remaining === null ? 2000 : 1000);

    return () => clearInterval(pollId);
  }, [remaining === null || remaining <= 0, getRemaining]);

  // Active countdown once we have a valid remaining value
  useEffect(() => {
    if (remaining === null) return;
    if (remaining <= 0) return;

    const id = setInterval(() => {
      const left = getRemaining();
      setRemaining(left);
      if (left <= 0) {
        clearInterval(id);
        if (onExpired && !firedRef.current) {
          firedRef.current = true;
          onExpired();
        }
      }
    }, 1000);

    return () => clearInterval(id);
  }, [remaining !== null]);

  useEffect(() => {
    if (remaining === 0 && onExpired && !firedRef.current) {
      firedRef.current = true;
      onExpired();
    }
  }, [remaining]);

  if (remaining === null) return { active: false };

  const totalSeconds = Math.ceil(remaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return {
    active: true,
    expired: remaining <= 0,
    minutes,
    seconds,
    totalSeconds,
    label: `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
  };
}

export function setSearchTimestamp(expiresAt) {
  if (typeof sessionStorage === 'undefined') return;

  if (expiresAt) {
    const expiryMs = Date.parse(expiresAt) || Number(expiresAt);
    if (Number.isFinite(expiryMs)) {
      sessionStorage.setItem(FLIGHT_SESSION_EXPIRES_AT_KEY, new Date(expiryMs).toISOString());
      sessionStorage.removeItem(FLIGHT_SEARCH_TIMESTAMP_KEY);
      setAllowedSessionExpiry(expiryMs);
    }
  }
}

export function setAllowedSessionExpiry(allowedExpiresAt) {
  if (typeof sessionStorage === 'undefined') return;
  if (!allowedExpiresAt) return;
  if (sessionStorage.getItem(FLIGHT_SESSION_ALLOWED_EXPIRES_AT_KEY)) return;

  const expiryMs = Date.parse(allowedExpiresAt) || Number(allowedExpiresAt);
  if (!Number.isFinite(expiryMs)) return;
  sessionStorage.setItem(FLIGHT_SESSION_ALLOWED_EXPIRES_AT_KEY, new Date(expiryMs).toISOString());
}

export function getAllowedSessionExpiresAtMs() {
  if (typeof sessionStorage === 'undefined') return null;
  const stored = sessionStorage.getItem(FLIGHT_SESSION_ALLOWED_EXPIRES_AT_KEY);
  if (!stored) return null;
  const expiryMs = Date.parse(stored);
  return Number.isFinite(expiryMs) ? expiryMs : null;
}

export function getRemainingSessionMs() {
  return getSessionRemainingMs({ product: SESSION_TIMER_PRODUCT.FLIGHT });
}

export function markFlightSessionExpired() {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(FLIGHT_SESSION_EXPIRES_AT_KEY, new Date().toISOString());
  sessionStorage.removeItem(FLIGHT_SEARCH_TIMESTAMP_KEY);
}

export function clearSearchTimestamp() {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(FLIGHT_SESSION_EXPIRES_AT_KEY);
  sessionStorage.removeItem(FLIGHT_SESSION_ALLOWED_EXPIRES_AT_KEY);
  sessionStorage.removeItem(FLIGHT_SEARCH_TIMESTAMP_KEY);
}

export function clearFlightSession() {
  const keysToRemove = [
    FLIGHT_SESSION_EXPIRES_AT_KEY,
    FLIGHT_SESSION_ALLOWED_EXPIRES_AT_KEY,
    FLIGHT_SEARCH_TIMESTAMP_KEY,
    'selectedFlight',
    'originalFlightOffer',
    'tbo_sessionId',
    'tbo_returnType',
  ];
  keysToRemove.forEach((k) => {
    try { sessionStorage.removeItem(k); } catch (_) { /* ignore */ }
  });
  try {
    Object.keys(sessionStorage).forEach((k) => {
      if (k.startsWith('flight_search_')) sessionStorage.removeItem(k);
    });
  } catch (_) { /* ignore */ }
}
